import { useState, useMemo } from 'react';
import { useApp } from '../../contexts/AppContext';
import { useTenant } from '../../contexts/TenantContext';
import { requestsService } from '../../services/requests.service';
import { assetsService } from '../../services/assets.service';

const PRIORITY_COLOR = { Low:'#607080',Normal:'#5b9cf6',High:'#f5a623',Urgent:'#e05757' };
const STATUS_COLOR   = { Pending:'#f5a623',Approved:'#00e5a0',Rejected:'#e05757',Completed:'#5b9cf6' };

function Chip({ label }) { const c=STATUS_COLOR[label]||'#607080'; return <span style={{ background:c+'18',border:`1px solid ${c}30`,color:c,borderRadius:6,padding:'2px 9px',fontSize:11,fontWeight:700 }}>{label}</span>; }
function Lbl({ children }) { return <div style={{ fontSize:10,color:'#3a5060',textTransform:'uppercase',letterSpacing:'1px',fontWeight:600,marginBottom:6 }}>{children}</div>; }
function Modal({ title, onClose, children }) {
  return <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,.88)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center',padding:20 }} onClick={e=>e.target===e.currentTarget&&onClose()}>
    <div style={{ background:'#090e15',border:'1px solid #1a2a3a',borderRadius:14,width:'100%',maxWidth:680,maxHeight:'90vh',overflow:'hidden',display:'flex',flexDirection:'column' }}>
      <div style={{ padding:'20px 24px',borderBottom:'1px solid #1a2a3a' }}>
        <div style={{ fontFamily:"'Syne',sans-serif",fontSize:16,fontWeight:800,color:'#e8edf2' }}>{title}</div>
      </div>
      <div style={{ padding:24,overflowY:'auto' }}>{children}</div>
    </div>
  </div>;
}

export default function RequestsPage() {
  const { requests, setRequests, assets, setAssets, facilities, profile, perms, showToast } = useApp();
  const { incrementUsage, reload: reloadTenant } = useTenant();
  const [modal,setModal]           = useState(null);
  const [form,setForm]             = useState({});
  const [search,setSearch]         = useState('');
  const [stFilter,setStFilter]     = useState('All');
  const [assetSearch,setAssetSearch] = useState('');
  const [saving,setSaving]         = useState(false);

  const pickableAssets = useMemo(()=>{
    const q=assetSearch.toLowerCase();
    return assets.filter(a=>a.status==='Available'&&(q===''||a.name?.toLowerCase().includes(q)||a.inventoryNumber?.toLowerCase().includes(q)||a.epc?.toLowerCase().includes(q)));
  },[assets,assetSearch]);

  const filtered = useMemo(()=>{
    let d=requests;
    if(stFilter!=='All') d=d.filter(r=>r.status===stFilter);
    if(search){ const q=search.toLowerCase(); d=d.filter(r=>r.id?.toLowerCase().includes(q)||r.assetName?.toLowerCase().includes(q)||r.requestedBy?.toLowerCase().includes(q)||r.inventoryNumber?.toLowerCase().includes(q)); }
    return [...d].sort((a,b)=>(b.requestedAt||'').localeCompare(a.requestedAt||''));
  },[requests,stFilter,search]);

  const counts = { Pending:requests.filter(r=>r.status==='Pending').length, Approved:requests.filter(r=>r.status==='Approved').length, Completed:requests.filter(r=>r.status==='Completed').length, Rejected:requests.filter(r=>r.status==='Rejected').length };

  const openNew = () => {
    setForm({ asset_id:'',asset_name:'',inventory_number:'',epc:'',from_facility_id:'',from_facility_name:'',to_facility_id:facilities[0]?.id||'',reason:'',priority:'Normal',notes:'' });
    setAssetSearch(''); setModal('new');
  };

  const selectAsset = (asset) => {
    const fromFac = facilities.find(f => f.id === asset.currentFacility);
    setForm(f=>({...f,
      asset_id:         asset.id,
      asset_name:       asset.name,
      inventory_number: asset.inventoryNumber,
      epc:              asset.epc,
      from_facility_id: asset.currentFacility,
      from_facility_name: fromFac?.name || asset.currentFacilityName || '',
      to_facility_id:   facilities.find(x => x.id !== asset.currentFacility)?.id || '',
    }));
    setAssetSearch(asset.name);
  };

  const submitRequest = async () => {
    if(!form.asset_id||!form.to_facility_id||!form.reason) return;
    if(form.from_facility_id===form.to_facility_id){ showToast('⚠ From and To facility must be different','error'); return; }
    setSaving(true);
    try {
      const toFac=facilities.find(f=>f.id===form.to_facility_id);
      const req=await requestsService.create({ ...form, to_facility_name:toFac?.name||'', requested_by:profile?.name, status:'Pending', org_id:profile?.org_id, requested_at:new Date().toISOString() });
      setRequests(prev=>[req,...prev]);
      showToast('✓ Transfer request submitted');
      try { await incrementUsage('requests'); } catch(e){} reloadTenant();
      setModal(null);
    } catch(err){ showToast('❌ '+err.message,'error'); }
    setSaving(false);
  };

  const approve = async (id) => {
    try {
      await requestsService.update(id,{ status:'Approved', approvedBy:profile?.name, approved_by:profile?.name });
      setRequests(prev=>prev.map(r=>r.id===id?{...r,status:'Approved',approved_by:profile?.name}:r));
      showToast('✓ Request approved');
    } catch(err){ showToast('❌ '+err.message,'error'); }
  };

  const reject = async (id) => {
    try {
      await requestsService.update(id,{ status:'Rejected', approvedBy:profile?.name, approved_by:profile?.name });
      setRequests(prev=>prev.map(r=>r.id===id?{...r,status:'Rejected',approved_by:profile?.name}:r));
      showToast('✓ Request rejected');
    } catch(err){ showToast('❌ '+err.message,'error'); }
  };

  const complete = async (row) => {
    try {
      const toFac=facilities.find(f=>f.id===(row.toFacilityId||row.to_facility_id));
      await requestsService.update(row.id,{ status:'Completed', approvedBy:profile?.name, approved_by:profile?.name });
      await assetsService.update(row.assetId||row.asset_id,{ current_facility:row.toFacilityId||row.to_facility_id, current_facility_name:toFac?.name||'', status:'Available', org_id:row.org_id });
      setRequests(prev=>prev.map(r=>r.id===row.id?{...r,status:'Completed'}:r));
      setAssets(prev=>prev.map(a=>a.id===(row.assetId||row.asset_id)?{...a,currentFacility:row.toFacilityId||row.to_facility_id,currentFacilityName:toFac?.name||'',status:'Available'}:a));
      showToast('✓ Transfer completed — asset moved');
    } catch(err){ showToast('❌ '+err.message,'error'); }
  };

  const STATUSES=['All','Pending','Approved','Rejected','Completed'];

  return <div>
    <div style={{ display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:24 }}>
      <div>
        <div style={{ fontFamily:"'Syne',sans-serif",fontSize:22,fontWeight:800,color:'#e8edf2' }}>Transfer Requests</div>
        <div style={{ fontSize:12,color:'#3a5060',marginTop:4 }}>{counts.Pending} pending approval · {requests.length} total</div>
      </div>
      <button className="btn" style={{ background:'#00e5a0',color:'#060b10' }} onClick={openNew}>+ New Request</button>
    </div>

    <div style={{ display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:14,marginBottom:24 }}>
      {[{label:'Pending',value:counts.Pending,icon:'⏳',c:'#f5a623'},{label:'Approved',value:counts.Approved,icon:'✓',c:'#00e5a0'},{label:'Completed',value:counts.Completed,icon:'⇄',c:'#5b9cf6'},{label:'Rejected',value:counts.Rejected,icon:'✕',c:'#e05757'}].map(s=>(
        <div key={s.label} style={{ background:'#0d1520',border:'1px solid #1a2a3a',borderRadius:12,padding:20 }}>
          <div style={{ display:'flex',alignItems:'center',gap:10,marginBottom:10 }}>
            <div style={{ width:36,height:36,borderRadius:9,background:s.c+'18',border:`1px solid ${s.c}30`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:18 }}>{s.icon}</div>
            <div style={{ fontSize:10,color:'#3a5060',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.6px' }}>{s.label}</div>
          </div>
          <div style={{ fontSize:28,fontWeight:800,color:'#e8edf2',fontFamily:"'Syne',sans-serif" }}>{s.value}</div>
        </div>
      ))}
    </div>

    <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14,flexWrap:'wrap',gap:10 }}>
      <div style={{ display:'flex',gap:2 }}>
        {STATUSES.map(s=><button key={s} className="tab-btn" onClick={()=>setStFilter(s)} style={{ color:stFilter===s?(STATUS_COLOR[s]||'#00e5a0'):'#3a5060',borderBottom:stFilter===s?`2px solid ${STATUS_COLOR[s]||'#00e5a0'}`:'2px solid transparent' }}>
          {s}<span style={{ marginLeft:5,background:'#1a2a3a',borderRadius:8,padding:'1px 6px',fontSize:10,color:'#607080' }}>{s==='All'?requests.length:requests.filter(r=>r.status===s).length}</span>
        </button>)}
      </div>
      <div style={{ position:'relative' }}>
        <span style={{ position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',color:'#3a5060' }}>🔍</span>
        <input className="inp" style={{ paddingLeft:30,width:240 }} placeholder="Search ID, asset, requester…" value={search} onChange={e=>setSearch(e.target.value)}/>
      </div>
    </div>

    <div style={{ background:'#0d1520',border:'1px solid #1a2a3a',borderRadius:12,overflow:'hidden' }}>
      <table style={{ width:'100%',borderCollapse:'collapse' }}>
        <thead><tr style={{ borderBottom:'1px solid #1a2a3a' }}>
          {['Request ID','Inventory No','Asset','From','To','Reason','Priority','Status','Requested By','Date','Actions'].map(h=><th key={h} className="th">{h}</th>)}
        </tr></thead>
        <tbody>
          {filtered.length===0&&<tr><td colSpan={11} style={{ textAlign:'center',padding:'40px 0',color:'#3a5060' }}>No transfer requests found</td></tr>}
          {filtered.map((r,i)=><tr key={r.id} className="row" style={{ borderBottom:i<filtered.length-1?'1px solid #0d1520':'none' }}>
            <td className="td"><span style={{ fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:'#5b9cf6' }}>{r.id?.slice(0,8)}</span></td>
            <td className="td"><span style={{ fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:'#f5a623' }}>{r.inventoryNumber}</span></td>
            <td className="td"><span style={{ fontWeight:600,color:'#e8edf2' }}>{r.assetName}</span></td>
            <td className="td"><span style={{ fontSize:12,color:'#607080' }}>{r.fromFacilityName}</span></td>
            <td className="td"><span style={{ fontSize:12,color:'#c8d6e0' }}>{r.toFacilityName}</span></td>
            <td className="td"><span style={{ fontSize:12,color:'#607080',maxWidth:140,display:'block',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{r.reason}</span></td>
            <td className="td"><span style={{ color:PRIORITY_COLOR[r.priority]||'#607080',fontWeight:600,fontSize:12 }}>{r.priority}</span></td>
            <td className="td"><Chip label={r.status}/></td>
            <td className="td"><span style={{ fontSize:12 }}>{r.requestedBy}</span></td>
            <td className="td"><span style={{ fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:'#3a5060' }}>{r.requestedAt?.slice(0,10)}</span></td>
            <td className="td" onClick={e=>e.stopPropagation()}>
              {r.status==='Pending'&&perms.canApprove&&<div style={{ display:'flex',gap:5 }}>
                <button className="btn" style={{ background:'#e0575720',border:'1px solid #e0575740',color:'#e05757',padding:'4px 9px',fontSize:11 }} onClick={()=>reject(r.id)}>✕</button>
                <button className="btn" style={{ background:'#00e5a020',border:'1px solid #00e5a040',color:'#00e5a0',padding:'4px 9px',fontSize:11 }} onClick={()=>approve(r.id)}>✓</button>
              </div>}
              {r.status==='Approved'&&perms.canApprove&&<button className="btn" style={{ background:'#5b9cf620',border:'1px solid #5b9cf640',color:'#5b9cf6',padding:'4px 9px',fontSize:11 }} onClick={()=>complete(r)}>⇄ Transfer</button>}
              {['Completed','Rejected'].includes(r.status)&&<span style={{ fontSize:11,color:'#3a5060' }}>{r.approvedBy?`by ${r.approvedBy}`:''}</span>}
            </td>
          </tr>)}
        </tbody>
      </table>
    </div>

    {modal==='new'&&<Modal title="New Transfer Request" onClose={()=>setModal(null)}>
      <div style={{ display:'flex',flexDirection:'column',gap:16 }}>
        <div>
          <Lbl>Search & Select Asset *</Lbl>
          <input className="inp" value={assetSearch} placeholder="Type asset name, inventory no., or EPC…" onChange={e=>{setAssetSearch(e.target.value);setForm(f=>({...f,asset_id:'',asset_name:''}));}}/>
          {assetSearch&&!form.asset_id&&<div style={{ background:'#0d1520',border:'1px solid #1a2a3a',borderRadius:8,marginTop:6,maxHeight:200,overflowY:'auto' }}>
            {pickableAssets.length===0?<div style={{ padding:'20px',textAlign:'center',color:'#3a5060',fontSize:12 }}>No available assets found</div>
            :pickableAssets.map(a=><div key={a.id} style={{ padding:'10px 14px',cursor:'pointer',borderBottom:'1px solid #1a2a3a' }} onClick={()=>selectAsset(a)}
              onMouseEnter={e=>e.currentTarget.style.background='#1a2a3a'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
              <div style={{ fontWeight:600,fontSize:13,color:'#e8edf2' }}>{a.name}</div>
              <div style={{ fontSize:11,color:'#3a5060',marginTop:2,fontFamily:"'IBM Plex Mono',monospace" }}>{a.inventoryNumber} · {a.currentFacilityName}</div>
            </div>)}
          </div>}
          {form.asset_id&&<div style={{ background:'#00e5a010',border:'1px solid #00e5a030',borderRadius:8,padding:'10px 14px',marginTop:8,fontSize:12,color:'#00e5a0' }}>
            ✓ Selected: <strong>{form.asset_name}</strong> from <strong>{form.from_facility_name}</strong>
          </div>}
        </div>
        <div>
          <Lbl>Transfer To *</Lbl>
          <select className="sel" style={{ width:'100%' }} value={form.to_facility_id} onChange={e=>setForm(f=>({...f,to_facility_id:e.target.value}))}>
            {facilities.filter(f=>f.id!==form.from_facility_id).map(f=><option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </div>
        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12 }}>
          <div><Lbl>Priority</Lbl><select className="sel" style={{ width:'100%' }} value={form.priority} onChange={e=>setForm(f=>({...f,priority:e.target.value}))}>
            {['Low','Normal','High','Urgent'].map(p=><option key={p}>{p}</option>)}
          </select></div>
        </div>
        <div><Lbl>Reason *</Lbl><input className="inp" value={form.reason||''} placeholder="Why is this transfer needed?" onChange={e=>setForm(f=>({...f,reason:e.target.value}))}/></div>
        <div><Lbl>Notes</Lbl><textarea className="inp" style={{ resize:'vertical',minHeight:60 }} value={form.notes||''} onChange={e=>setForm(f=>({...f,notes:e.target.value}))}/></div>
      </div>
      <div style={{ display:'flex',gap:10,marginTop:20 }}>
        <button className="btn" style={{ flex:1,background:'none',border:'1px solid #1a2a3a',color:'#607080' }} onClick={()=>setModal(null)}>Cancel</button>
        <button className="btn" style={{ flex:2,background:'#00e5a0',color:'#060b10' }} onClick={submitRequest} disabled={saving||!form.asset_id||!form.reason}>{saving?'Submitting…':'Submit Request'}</button>
      </div>
    </Modal>}
  </div>;
}
